import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://www.linatravelina.eu";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

export default function SEO({ 
  title, 
  description, 
  keywords, 
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false 
}) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language || "hr";
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  const resolvedImage =
    typeof image === "string" && image.startsWith("http")
      ? image
      : `${SITE_URL}${image?.startsWith("/") ? image : "/logo512.png"}`;
  const imageType = resolvedImage.toLowerCase().endsWith(".png")
    ? "image/png"
    : "image/jpeg";

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute("content", content);
    };

    // Basic SEO
    if (description) {
      updateMetaTag("description", description);
    }

    if (keywords) {
      updateMetaTag("keywords", keywords);
    }

    // Open Graph / WhatsApp
    updateMetaTag("og:title", title || "TraveLina - Putovanja u Španjolsku", true);
    updateMetaTag("og:description", description || "Putovanja u manjim grupama, personalizirane ture i potpuna podrška na hrvatskom i španjolskom jeziku.", true);
    updateMetaTag("og:image", resolvedImage, true);
    updateMetaTag("og:image:secure_url", resolvedImage, true);
    updateMetaTag("og:image:type", imageType, true);
    updateMetaTag("og:image:width", "1200", true);
    updateMetaTag("og:image:height", "630", true);
    updateMetaTag("og:image:alt", title || "TraveLina - Putovanja u Španjolsku", true);
    updateMetaTag("og:image:url", resolvedImage, true);
    updateMetaTag("og:url", canonicalUrl, true);
    updateMetaTag("og:type", type, true);
    updateMetaTag("og:locale", lang === "hr" ? "hr_HR" : lang === "en" ? "en_US" : lang === "de" ? "de_DE" : "es_ES", true);
    updateMetaTag("og:site_name", "TraveLina", true);

    // Twitter Card
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title || "TraveLina - Putovanja u Španjolsku");
    updateMetaTag("twitter:description", description || "Putovanja u manjim grupama, personalizirane ture i potpuna podrška na hrvatskom i španjolskom jeziku.");
    updateMetaTag("twitter:image", resolvedImage);
    updateMetaTag("twitter:url", canonicalUrl);

    // GEO tags
    updateMetaTag("geo.region", "HR-17"); // Primorsko-goranska županija
    updateMetaTag("geo.placename", "Cesarica, Primorska 1");
    updateMetaTag("geo.position", "45.1234;14.5678"); // Približne koordinate za Cesaricu
    updateMetaTag("ICBM", "45.1234, 14.5678");

    // Language
    updateMetaTag("language", lang);
    
    // Canonical URL
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Alternate languages
    const alternateLangs = [
      { lang: "hr", url: `${SITE_URL}${location.pathname}?lang=hr` },
      { lang: "en", url: `${SITE_URL}${location.pathname}?lang=en` },
      { lang: "de", url: `${SITE_URL}${location.pathname}?lang=de` },
      { lang: "es", url: `${SITE_URL}${location.pathname}?lang=es` },
    ];

    // Remove existing alternate links
    document.querySelectorAll("link[rel='alternate'][hreflang]").forEach(el => el.remove());

    // Add alternate links
    alternateLangs.forEach(({ lang, url }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", url);
      document.head.appendChild(link);
    });

    // Robots
    if (noindex) {
      updateMetaTag("robots", "noindex, nofollow");
    } else {
      updateMetaTag("robots", "index, follow");
    }

    // Structured Data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "TravelAgency",
      "name": "TraveLina",
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo512.png`,
      "description": "Agencija za putovanja TraveLina - Putovanja u Španjolsku i španjolski govorni prostor. Personalizirane ture, učenje španjolskog jezika, gastronomska iskustva i autentična putovanja.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Primorska 1",
        "addressLocality": "Cesarica",
        "addressCountry": "HR"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+385-91-214-4477",
        "contactType": "customer service",
        "email": "Info@linatravelina.eu",
        "availableLanguage": ["hr", "en", "de", "es"]
      },
      "areaServed": {
        "@type": "Country",
        "name": "Croatia"
      },
      "sameAs": [
        // Dodajte linkove na društvene mreže ako ih imate
      ]
    };

    // Remove existing structured data
    let existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

  }, [title, description, keywords, image, resolvedImage, imageType, type, canonicalUrl, lang, noindex, location.pathname]);

  return null;
}

